import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, catchError, finalize, map, Observable, of, shareReplay, tap } from 'rxjs';

import { SUDEBAN_BANKS_WITH_CODE, SudebanBank } from '../constants/sudeban-banks';
import {
    AbonarBody,
    AbonarResponse,
    BanescoTestData,
    CreditLine,
    CreditLineContract,
    CreditLineErrorCode,
    CreditLineResponse,
    CreditLineVersion,
    Credito,
    Installment,
    LineRequestBody,
    LineRequestResponse,
    PayInstallmentBody,
    PayInstallmentResponse,
    RecalcRequestResponse,
    RequestLoanBody,
    ReverifyPaymentBody,
} from '../models';
import { ACTIVATION_FEE_USD, LATE_GRACE_CALENDAR_DAYS } from '../utils/abono-plan.util';

/**
 * Política de mora alineada con api/src/constants/creditosLateFee.ts (FINA-2062 v2):
 *   - 3 días calendario de gracia tras `dueDate` (sin cargo).
 *   - A partir del día 4 (cualquier cuota): el crédito está BLOQUEADO.
 *     Para desbloquearse: pagar la cuota vencida + ACTIVATION_FEE_USD ($15).
 *
 * Nota: la fuente de verdad del estado de bloqueo es el backend
 * (`GET /api/creditos/access-status`). Estos helpers client-side existen
 * solo para banners/labels antes de que llegue la respuesta — el bloqueo real
 * lo dispara el interceptor cuando el backend devuelve 423.
 *
 * Las constantes viven en `utils/abono-plan.util.ts` (espejo puro del backend) y se
 * re-exportan acá para no romper los imports existentes.
 */
export { ACTIVATION_FEE_USD, LATE_GRACE_CALENDAR_DAYS };

/** Estado de bloqueo/mora del crédito vigente — shape consumido por banners y modal de bloqueo. */
export interface CreditAccessStatus {
    hasActiveCredit: boolean;
    creditId?: string;
    blocked: boolean;
    /** true si la cuota que carga el fee ya está cubierta y espera verificación de soporte (Abonos). */
    pendingVerification?: boolean;
    /** Fee que todavía se debe: 0 si no está bloqueado o si los abonos ya lo cubrieron. */
    activationFee: number;
    gracePeriodDays: number;
    daysToNextDue: number | null; // ≥0 (próxima cuota a vencer)
    daysOverdue: number; // 0 si nada vencido, ≥1 si hay vencidas
    /** Remanente del monto de la cuota a pagar (neto de abonos, sin fee). */
    dueAmount: number;
    dueDate: string | null;
    dueInstallmentNumber?: number | null;
    firstOverdueInstallmentNumber: number | null;
    feeCarrierInstallmentNumber?: number | null;
    upcomingInstallmentNumber: number | null;
    paymentDetails: any;
    /** Todo lo que hay que pagar hoy para desbloquear (fee incluido). null si no está bloqueado. */
    totalToUnblock: number | null;
}

const EMPTY_ACCESS_STATUS: CreditAccessStatus = {
    hasActiveCredit: false,
    blocked: false,
    pendingVerification: false,
    activationFee: 0,
    gracePeriodDays: LATE_GRACE_CALENDAR_DAYS,
    daysToNextDue: null,
    daysOverdue: 0,
    dueAmount: 0,
    dueDate: null,
    dueInstallmentNumber: null,
    firstOverdueInstallmentNumber: null,
    feeCarrierInstallmentNumber: null,
    upcomingInstallmentNumber: null,
    paymentDetails: null,
    totalToUnblock: null,
};

@Injectable({ providedIn: 'root' })
export class CreditosService {
    private API_URL = environment.API_URL;

    _credito$: BehaviorSubject<Credito | null> = new BehaviorSubject<Credito | null>(null);
    /** Estado de bloqueo computado por el backend — fuente única de verdad. */
    _accessStatus$: BehaviorSubject<CreditAccessStatus> = new BehaviorSubject<CreditAccessStatus>(EMPTY_ACCESS_STATUS);

    constructor(private http: HttpClient) {}

    get credito$() {
        return this._credito$;
    }

    set credito(data: Credito | null) {
        this._credito$.next(data ?? null);
    }

    get accessStatus$(): Observable<CreditAccessStatus> {
        return this._accessStatus$.asObservable();
    }

    // ─── Helpers de MORA (client-side, derivados de dueDate) ──────────────────────────────────

    /**
     * Días calendario de retraso desde dueDate (0 si aún no vencida o ya pagada/verifying).
     *
     * QA UX/UI #9 — alineado con la lógica server-side `calendarLateDays` del
     * `api/src/constants/creditosLateFee.ts` y con `accessStatus.daysOverdue`
     * que alimenta al banner. Antes este getter usaba `Math.ceil((now - due) / 1day)`
     * que sumaba fracciones de día (por horas) — si la cuota vencía 06/may 00:00 y
     * hoy era 08/may 12:00, la diff en horas era ~60 → `ceil(60/24)=3`, mientras
     * que el backend (día calendario `startOfDay` → diff → round) daba 2 días.
     * Resultado: el banner mostraba "venció hace 2 días" y la tarjeta de cuota
     * mostraba "3 días de retraso" para el mismo crédito. Ahora ambos coinciden.
     */
    static computeLateDays(inst: Installment | null | undefined): number {
        if (!inst?.dueDate) return 0;
        if (inst.status === 'paid' || inst.status === 'verifying') return 0;
        const startOfDay = (d: Date) => {
            const x = new Date(d);
            x.setHours(0, 0, 0, 0);
            return x.getTime();
        };
        const dueDay = startOfDay(new Date(inst.dueDate));
        const todayDay = startOfDay(new Date());
        if (todayDay <= dueDay) return 0;
        return Math.round((todayDay - dueDay) / (1000 * 60 * 60 * 24));
    }

    /**
     * Fee USD por cuota individual.
     * Bajo la política nueva, el fee es por crédito (no por cuota), así que
     * a nivel cuota siempre devuelve 0. Use `computeActivationFee(credito)`
     * para el fee de bloqueo a nivel crédito.
     */
    static computeLateFee(_inst: Installment | null | undefined): number {
        return 0;
    }

    /** ¿Esta cuota está vencida pero todavía dentro del período de gracia (1–3 días)? */
    static isInstallmentInGrace(inst: Installment | null | undefined): boolean {
        const dl = CreditosService.computeLateDays(inst);
        return dl > 0 && dl <= LATE_GRACE_CALENDAR_DAYS;
    }

    /** ¿Esta cuota pasó la gracia (4to día o más sin pagar)? Dispara el bloqueo. */
    static isInstallmentBlocking(inst: Installment | null | undefined): boolean {
        return CreditosService.computeLateDays(inst) > LATE_GRACE_CALENDAR_DAYS;
    }

    /** Devuelve la primera cuota vencida (en gracia o bloqueante). */
    static findLateInstallment(credito: Credito | null): Installment | null {
        if (!credito || !credito.installments) return null;
        return credito.installments.find(i => CreditosService.computeLateDays(i) > 0) || null;
    }

    static hasLateInstallment(credito: Credito | null): boolean {
        return !!CreditosService.findLateInstallment(credito);
    }

    /**
     * ¿Se ve `fina-credit-due-banner`? Solo con crédito activo y la cuota dentro de [-3, +3], o
     * bloqueado. Predicado único: lo usan ese banner y el Home (FINA-3378), que no pinta el banner
     * de Fina Presta mientras el de la cuota está visible.
     */
    static isDueBannerVisible(status: CreditAccessStatus | null): boolean {
        if (!status?.hasActiveCredit) return false;
        if (status.blocked) return true; // también para quien vio el modal de bloqueo y lo cerró
        if (status.daysOverdue >= 1 && status.daysOverdue <= 3) return true;
        if (status.daysToNextDue !== null && status.daysToNextDue >= 0 && status.daysToNextDue <= 3) return true;
        return false;
    }

    /**
     * ¿La oferta `eligible` ya venció? (expiresAt en el pasado). Solo aplica a
     * status 'eligible' — los demás estados no tienen semántica de vencimiento
     * de OFERTA (el vencimiento de cuotas es computeLateDays). Fuente única de
     * verdad para ocultar banner/modal/CTAs de oferta vencida en home y landing.
     */
    static isOfferExpired(credito: Credito | null | undefined): boolean {
        if (!credito || credito.status !== 'eligible' || !credito.expiresAt) return false;
        const t = new Date(credito.expiresAt).getTime();
        return Number.isFinite(t) && t <= Date.now();
    }

    /** ¿El crédito está bloqueado? (al menos una cuota >3 días vencida). Inferencia client-side; backend manda. */
    static isCreditBlocked(credito: Credito | null): boolean {
        if (!credito || !credito.installments) return false;
        return credito.installments.some(i => CreditosService.isInstallmentBlocking(i));
    }

    /** Fee de activación que el cliente debe pagar para desbloquear (0 si no bloqueado). */
    static computeActivationFee(credito: Credito | null): number {
        return CreditosService.isCreditBlocked(credito) ? ACTIVATION_FEE_USD : 0;
    }

    /** Observable derivado: true si hay al menos una cuota en mora (en gracia o bloqueante). */
    get hasLateCredito$(): Observable<boolean> {
        return this._credito$.pipe(map(c => CreditosService.hasLateInstallment(c)));
    }

    /** Observable derivado: true si el crédito está bloqueado (4+ días vencido). */
    get isBlocked$(): Observable<boolean> {
        return this._accessStatus$.pipe(map(s => s.blocked));
    }

    // ─── HTTP ──────────────────────────────────

    getCurrent$(): Observable<any> {
        return this.http.get<any>(`${this.API_URL}/creditos/current`);
    }

    // ─── Línea de crédito v2 (FINA-3378) ─────────────────────────────────────────────────────

    /**
     * Estado de la línea por instancia del servicio (como `_credito$` y `_accessStatus$`): `null`
     * = todavía no se leyó o no se pudo leer; `{ version: 'v1' }` = v2 apagada; `CreditLine` = v2
     * encendida. Lo publica `refreshLine$()`; los componentes leen `line$` / `isV2$`.
     *
     * Ojo: `CreditosModule` (lazy) provee su propia instancia, así que el módulo de créditos y el
     * home (instancia root) no comparten este estado: cada uno lee la línea en su lado.
     */
    private _line$ = new BehaviorSubject<CreditLineResponse['data'] | null>(null);
    /** Lectura de `GET /line` en vuelo, compartida por `knownLine$()`. */
    private lineInFlight$: Observable<CreditLineResponse['data']> | null = null;

    get line$(): Observable<CreditLineResponse['data'] | null> {
        return this._line$.asObservable();
    }

    /** `true` solo si la última lectura de `GET /line` dijo `v2`. */
    get isV2$(): Observable<boolean> {
        return this._line$.pipe(map(data => CreditosService.lineVersionOf(data) === 'v2'));
    }

    /** `v2` solo si el servidor lo dice; cualquier otra respuesta es `v1`. */
    static lineVersionOf(data: CreditLineResponse['data'] | null | undefined): CreditLineVersion {
        return data?.version === 'v2' ? 'v2' : 'v1';
    }

    /** La línea v2, o null con la v2 apagada / sin leer. */
    static lineOf(data: CreditLineResponse['data'] | null | undefined): CreditLine | null {
        return data?.version === 'v2' ? (data as CreditLine) : null;
    }

    /** `code` de un error de los endpoints de la línea (`{ title, message, code }`), o null. */
    static lineErrorCode(err: any): CreditLineErrorCode | null {
        const code = err?.error?.code;
        return typeof code === 'string' ? (code as CreditLineErrorCode) : null;
    }

    /**
     * `GET /creditos/line` y publica el resultado en `line$`. Con la v2 apagada responde
     * `{ data: { version: 'v1' } }` (nunca 403), y responde también con el bloqueo 423 de mora.
     * Siempre pega al servidor: la línea cambia con cada pago verificado o pedido.
     */
    refreshLine$(): Observable<CreditLineResponse['data']> {
        return this.http.get<CreditLineResponse>(`${this.API_URL}/creditos/line`).pipe(
            map(res => res?.data ?? { version: 'v1' as const }),
            tap(data => this._line$.next(data)),
        );
    }

    /**
     * Para quien solo necesita la versión ante una oferta `eligible` (el home), sin pedir
     * `GET /line` en cada `visibilitychange` (con la v2 encendida esa lectura puede recalcular la
     * línea entera, QA 001#4). Reutiliza solo un `v1` ya leído; si no, hace UNA lectura compartida
     * entre quienes pregunten a la vez. Un `v2` no se reutiliza: con la v2 encendida no hay ofertas
     * vivas, así que ver una oferta es la señal de que la v2 se apagó (QA 002#3). Un fallo no queda
     * guardado.
     */
    knownLine$(): Observable<CreditLineResponse['data']> {
        const known = this._line$.value;
        if (known?.version === 'v1') return of(known);
        if (!this.lineInFlight$) {
            this.lineInFlight$ = this.refreshLine$().pipe(
                finalize(() => (this.lineInFlight$ = null)),
                shareReplay({ bufferSize: 1, refCount: true }),
            );
        }
        return this.lineInFlight$;
    }

    /** Olvida la línea leída: `isV2$` vuelve a `false` (el módulo sigue como v1). */
    resetLine(): void {
        this._line$.next(null);
    }

    /**
     * `GET /creditos/line/contract?amount=`: PDF sin firmar y cronograma autoritativo con los
     * términos del nivel. Se llama UNA sola vez, al confirmar el monto.
     */
    getLineContract$(amount: number): Observable<CreditLineContract> {
        return this.http.get<{ data: CreditLineContract }>(`${this.API_URL}/creditos/line/contract`, { params: { amount: String(amount) } }).pipe(map(res => res.data));
    }

    /** `POST /creditos/line/request`: pide un crédito desde la línea. El servidor firma el contrato. */
    requestLine$(body: LineRequestBody): Observable<LineRequestResponse> {
        return this.http.post<LineRequestResponse>(`${this.API_URL}/creditos/line/request`, body);
    }

    /**
     * `POST /creditos/line/recalc-request`: solicitud de recálculo con el mensaje y el monto que pide el
     * comercio (DT-42; `api` lo exige y lo valida: 400 `RECALC_REQUEST_AMOUNT_INVALID`).
     */
    recalcRequest$(message: string, requestedAmountUSD: number): Observable<RecalcRequestResponse> {
        return this.http.post<RecalcRequestResponse>(`${this.API_URL}/creditos/line/recalc-request`, { message, requestedAmountUSD });
    }

    /**
     * Trae el estado de bloqueo/mora computado por el backend y lo publica en `accessStatus$`.
     * Llamar al inicio de la sesión y después de cada pago.
     */
    refreshAccessStatus$(): Observable<CreditAccessStatus> {
        return this.http.get<{ data: CreditAccessStatus }>(`${this.API_URL}/creditos/access-status`).pipe(
            map(res => res?.data ?? EMPTY_ACCESS_STATUS),
            tap(status => this._accessStatus$.next(status)),
        );
    }

    requestLoan$(creditoId: string, body: RequestLoanBody): Observable<any> {
        return this.http.put<any>(`${this.API_URL}/creditos/${creditoId}/request`, body);
    }

    payInstallment$(creditoId: string, body: PayInstallmentBody & { comprobanteKey?: string }): Observable<PayInstallmentResponse> {
        return this.http.put<PayInstallmentResponse>(`${this.API_URL}/creditos/${creditoId}/pay`, body);
    }

    /**
     * ADM-678 (2ª iteración) — data de prueba de Banesco Calidad.
     *
     * Cacheada con `shareReplay(1)` porque la consumen tres componentes (modal de
     * pago, modal de bloqueo y formulario de solicitud) y el contenido no cambia
     * durante la sesión.
     *
     * Ante cualquier error se cae a `enabled: false`: la data de prueba es una
     * ayuda para QA, nunca un motivo para que el modal de pago no abra.
     */
    private testData$?: Observable<BanescoTestData>;

    banescoTestData$(): Observable<BanescoTestData> {
        if (!this.testData$) {
            this.testData$ = this.http.get<{ data: BanescoTestData }>(`${this.API_URL}/creditos/test-payments`).pipe(
                map(res => res?.data || { enabled: false }),
                catchError(() => of<BanescoTestData>({ enabled: false })),
                shareReplay({ bufferSize: 1, refCount: false }),
            );
        }
        return this.testData$;
    }

    /**
     * ADM-678 — bancos con código SUDEBAN para el selector del modal de pago.
     *
     * Si el endpoint falla se cae a la constante local: el selector no puede
     * quedar vacío, porque sin banco el cliente no puede notificar su pago.
     */
    banks$(): Observable<ReadonlyArray<SudebanBank>> {
        return this.http.get<{ data: SudebanBank[] }>(`${this.API_URL}/creditos/banks`).pipe(
            map(res => (res?.data?.length ? res.data : SUDEBAN_BANKS_WITH_CODE)),
            catchError(() => of(SUDEBAN_BANKS_WITH_CODE)),
        );
    }

    /**
     * Abonos: el comercio abona parte de una cuota (o más de una: el sobrante pasa a la
     * siguiente). Se aplica de inmediato en el backend; la respuesta trae el crédito
     * actualizado y el reparto cuota por cuota (`applied`). `body.groupId` es la clave de
     * idempotencia: reutilizarlo en un retry no duplica el abono.
     */
    abonar$(creditoId: string, body: AbonarBody): Observable<AbonarResponse> {
        return this.http.put<AbonarResponse>(`${this.API_URL}/creditos/${creditoId}/abonar`, body);
    }

    /**
     * "Pagos por validar": corrige o reenvía al banco un abono del comercio aplicado sin validar.
     * La respuesta tiene la forma de `/pay` (crédito actualizado + `verification`).
     */
    verifyAbono$(creditoId: string, groupId: string, body: ReverifyPaymentBody): Observable<PayInstallmentResponse> {
        return this.http.put<PayInstallmentResponse>(`${this.API_URL}/creditos/${creditoId}/abonos/${groupId}/verify`, body);
    }

    /**
     * "Pagos por validar": corrige o reenvía al banco el pago de una cuota que quedó en
     * verificación sin confirmar. Una cuota en soporte se corrige con `payInstallment$`.
     */
    verifyInstallmentPayment$(creditoId: string, installmentNumber: number, body: ReverifyPaymentBody): Observable<PayInstallmentResponse> {
        return this.http.put<PayInstallmentResponse>(`${this.API_URL}/creditos/${creditoId}/installments/${installmentNumber}/verify`, body);
    }

    /**
     * Sube comprobante a S3 (bucket privado fina-loans). Retorna:
     *   - `key`: persistente, es lo que el frontend manda al endpoint de pago
     *   - `publicUrl`: presigned 1 h, solo para preview inmediato (no persistir)
     */
    uploadComprobante$(file: File, loanId: string): Observable<{ key: string; publicUrl: string }> {
        const form = new FormData();
        form.append('file', file);
        form.append('loanId', loanId);
        return this.http.post<any>(`${this.API_URL}/creditos/upload-comprobante`, form).pipe(map(res => ({ key: res.key, publicUrl: res.publicUrl })));
    }

    /**
     * @deprecated Hotfix funcional FINA-2062 (bug #3): el endpoint del backend ahora
     * devuelve 501 — el siguiente nivel se asigna manualmente por el equipo de Fina.
     * El método queda como stub para no romper imports legacy. NO LO LLAMES.
     *
     * Si por error se llama, devuelve un Observable que emite el 501 (mismo formato
     * de error que cualquier otro endpoint). El completion-modal y el next-level-teaser
     * ya no dependen de este método.
     */
    unlockNextLevel$(): Observable<any> {
        return this.http.post<any>(`${this.API_URL}/creditos/unlock-next-level`, {});
    }

    /**
     * FINA-2062: contrato personalizado por usuario.
     * Genera el PDF del contrato (template + datos del owner+credito), lo sube a S3 y
     * devuelve presigned URL (1h) para mostrarlo en el modal con `<ngx-extended-pdf-viewer>`.
     * `tentativeAmount`: monto que el user tiene seleccionado en el form para que el contrato
     * preview refleje los términos correctos antes de la solicitud formal (status=eligible
     * todavía no tiene `amount` en DB). Si no se pasa, el backend usa `maxAmount` del crédito.
     */
    generateContract$(creditoId: string, tentativeAmount?: number): Observable<{ key: string; url: string }> {
        const url = tentativeAmount != null ? `${this.API_URL}/creditos/${creditoId}/contract?amount=${tentativeAmount}` : `${this.API_URL}/creditos/${creditoId}/contract`;
        return this.http.get<any>(url).pipe(map(res => ({ key: res.data.key, url: res.data.url })));
    }

    /**
     * FINA-2062: el user (owner) acepta el contrato tras hacer scroll completo.
     * Backend persiste timestamp + IP del request y re-firma el PDF en S3 (página 10
     * con Constancia de Aceptación llena). Pasa el `tentativeAmount` para mantener
     * consistencia con el preview pre-aceptación.
     */
    acceptContract$(creditoId: string, tentativeAmount?: number): Observable<{ ok: true; signedKey: string; acceptedAt: string; ip: string }> {
        const url = tentativeAmount != null ? `${this.API_URL}/creditos/${creditoId}/accept-contract?amount=${tentativeAmount}` : `${this.API_URL}/creditos/${creditoId}/accept-contract`;
        return this.http.put<any>(url, {}).pipe(map(res => res.data));
    }
}
